/** CFO & Advisory: the portfolio of advisory clients, each with its latest headline figures and what needs attention. */
import { formatCents } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { errorMessage } from "@/lib/api";
import { AlertTriangle, ArrowRight, Building2, CalendarDays, CheckCircle2, FileText, User } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { Empty, ListSearch, Loading, matches, PageHeader, Pager, StatRow, usePaged } from "@/components/page";
import { Sparkline } from "@/components/sparkline";
import { formatDate, localToday } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import { cn } from "@/lib/utils";

type Row = {
  id: string;
  displayName: string;
  kind: "individual" | "business";
  assignedUserId: string | null;
  period: string | null;
  preliminary: boolean;
  revenue: number | null;
  netIncome: number | null;
  cash: number | null;
  netMargin: string | null;
  runway: { display: string; note: string | null; notBurning: boolean } | null;
  trend: Array<{ key: string; revenue: number; netIncome: number }>;
  lights: { green: number; amber: number; red: number };
  alerts: { critical: number; warning: number };
  report: { period: string; status: "draft" | "approved" | "published" } | null;
  nextMeeting: string | null;
};

const monthName = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

function Figure({ label, value, tone, title }: { label: string; value: string; tone?: "bad"; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn("tabular mt-0.5 break-words text-sm font-semibold", tone === "bad" ? "text-red-700 dark:text-red-400" : "text-foreground")}>{value}</dd>
    </div>
  );
}

function ClientCard({ r, manager }: { r: Row; manager: string }) {
  const reportLabel = !r.report ? "No report yet" : r.report.status === "published" ? `${monthName(r.report.period)} report published` : `${monthName(r.report.period)} report ${r.report.status === "approved" ? "approved, not published" : "in draft"}`;
  return (
    <article className="flex flex-col rounded-lg border border-card-border bg-card">
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <Link href={`/clients/${r.id}?tab=cfo`} className="flex items-center gap-2 font-semibold text-foreground hover:underline">
            {r.kind === "business" ? <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> : <User className="h-4 w-4 shrink-0 text-muted-foreground" />}
            <span className="truncate">{r.displayName}</span>
          </Link>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {manager}
            {r.period && (
              <>
                {" · "}
                {monthName(r.period)}
                {r.preliminary && <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">Preliminary</span>}
              </>
            )}
          </p>
        </div>
        <div role="group" className="flex shrink-0 items-center gap-1" aria-label="KPI targets">
          {r.lights.green > 0 && <span title="KPIs on target" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"><CheckCircle2 className="h-3 w-3" aria-hidden />{r.lights.green}<span className="sr-only"> on target</span></span>}
          {r.lights.amber > 0 && <span title="KPIs to watch" className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"><AlertTriangle className="h-3 w-3" aria-hidden />{r.lights.amber}<span className="sr-only"> to watch</span></span>}
          {r.lights.red > 0 && <span title="KPIs off target" className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-900/30 dark:text-red-200"><AlertTriangle className="h-3 w-3" aria-hidden />{r.lights.red}<span className="sr-only"> off target</span></span>}
        </div>
      </header>
      {!r.period ? (
        <p className="flex-1 px-4 py-6 text-sm text-muted-foreground">No monthly figures yet. Open the dashboard and refresh from QuickBooks.</p>
      ) : (
        <div className="flex-1 px-4 py-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Revenue</p>
              <p className="tabular text-xl font-semibold">{r.revenue === null ? "—" : formatCents(r.revenue, { showCents: false })}</p>
            </div>
            <Sparkline values={r.trend.map((t) => t.revenue)} className="h-10 w-32" />
          </div>
          {/* Two figures per row: a quarter of a three-across card cut figures ("$272,9…"); a figure is never cut. */}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
            <Figure label="Net income" value={r.netIncome === null ? "—" : formatCents(r.netIncome, { showCents: false })} tone={(r.netIncome ?? 0) < 0 ? "bad" : undefined} />
            <Figure label="Net margin" value={r.netMargin ?? "—"} />
            <Figure label="Cash" value={r.cash === null ? "—" : formatCents(r.cash, { showCents: false })} />
            {/* "Not burning cash" doesn't fit a quarter of a card. */}
            <Figure
              label="Runway at current burn"
              value={r.runway?.notBurning ? "Cash up" : (r.runway?.display ?? "—")}
              title={r.runway?.notBurning ? "Not burning cash: cash grew or held steady over the last three months" : (r.runway?.note ?? undefined)}
            />
          </dl>
        </div>
      )}
      <footer className="space-y-1.5 border-t border-border bg-secondary/40 px-4 py-2.5 text-xs">
        {(r.alerts.critical > 0 || r.alerts.warning > 0) && (
          <p className={cn("flex items-center gap-1.5 font-medium", r.alerts.critical ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-300")}>
            <AlertTriangle className="h-3.5 w-3.5" />
            {[r.alerts.critical && `${r.alerts.critical} critical exception${r.alerts.critical > 1 ? "s" : ""}`, r.alerts.warning && `${r.alerts.warning} exception${r.alerts.warning > 1 ? "s" : ""} to watch`].filter(Boolean).join(", ")}
          </p>
        )}
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <FileText className="h-3.5 w-3.5" /> {reportLabel}
        </p>
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" /> {r.nextMeeting ? `Next meeting ${formatDate(r.nextMeeting)}` : "No meeting planned"}
          </p>
          <Link href={`/clients/${r.id}?tab=cfo`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
            Dashboard <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </footer>
    </article>
  );
}

export function AdvisoryClientsPage() {
  const { data, isLoading, error } = useQuery<Row[]>({ queryKey: ["/api/cfo/portfolio"] });
  const names = useMemberNames();
  const rows = data ?? [];
  const today = localToday();
  const inTwoWeeks = localToday(new Date(Date.now() + 14 * 86_400_000));
  const [search, setSearch] = useState("");
  const manager = (r: Row) => (r.assignedUserId ? (names.get(r.assignedUserId) ?? "—") : "Unassigned");
  const shown = rows.filter((r) => matches(search, r.displayName, manager(r)));
  const paged = usePaged(shown, { resetKey: search });
  return (
    <>
      <PageHeader crumbs={[{ label: "CFO & Advisory", href: "/advisory" }]} title="Client Dashboards" description="Every advisory client's latest month at a glance. Open a client for the full dashboard, budget, forecasts, reports and meetings." />
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !rows.length ? (
        <Empty>No active clients are engaged for CFO & Advisory services.</Empty>
      ) : (
        <>
          <StatRow
            stats={[
              { label: "Advisory clients", value: rows.length },
              { label: "Open exceptions", value: rows.reduce((a, r) => a + r.alerts.critical + r.alerts.warning, 0), tone: rows.some((r) => r.alerts.critical) ? "danger" : rows.some((r) => r.alerts.warning) ? "warning" : "default" },
              { label: "KPIs off target", value: rows.reduce((a, r) => a + r.lights.red, 0), tone: rows.some((r) => r.lights.red) ? "warning" : "success" },
              { label: "Meetings in the next 14 days", value: rows.filter((r) => r.nextMeeting && r.nextMeeting >= today && r.nextMeeting <= inTwoWeeks).length },
            ]}
          />
          <ListSearch value={search} onChange={setSearch} placeholder="Search by client or manager" className="mb-4" />
          {!shown.length ? (
            <Empty>No advisory clients match the search.</Empty>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {paged.items.map((r) => (
                <ClientCard key={r.id} r={r} manager={manager(r)} />
              ))}
            </div>
          )}
          <Pager paged={paged} noun="clients" className="mt-4" />
        </>
      )}
    </>
  );
}
